import { useState } from "react";
import Navbar from "@/components/layout/Navbar";
import Footer from "@/components/layout/Footer";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useLanguage } from "@/contexts/LanguageContext";
import { useAuth } from "@/hooks/useAuth";
import { Mail, Facebook, MessageSquare, Send, Loader2, Link as LinkIcon } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

const Contact = () => {
    const { t } = useLanguage();
    const { user, profile } = useAuth();
    const [loading, setLoading] = useState(false);
    const [formData, setFormData] = useState({
        subject: "",
        message: "",
        email: profile?.email || user?.email || ""
    });

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!formData.subject || !formData.message || !formData.email) {
            toast.error(t("กรุณากรอกข้อมูลให้ครบถ้วน", "Please fill in all fields"));
            return;
        }

        setLoading(true);
        try {
            // We'll attempt to save to support_tickets table. 
            // If it doesn't exist, this will error, but we handle it.
            const { error } = await supabase
                .from("support_tickets" as any)
                .insert([
                    {
                        user_id: user?.id,
                        email: formData.email,
                        subject: formData.subject,
                        message: formData.message,
                        status: "pending"
                    }
                ]);

            if (error) throw error;

            toast.success(t("ส่งข้อความสำเร็จ ทีมงานจะตอบกลับโดยเร็วที่สุด", "Message sent successfully! Our team will get back to you soon."));
            setFormData(prev => ({ ...prev, subject: "", message: "" }));
        } catch (error: any) {
            console.error("Support submission error:", error);
            toast.error(t("เกิดข้อผิดพลาดในการส่งข้อมูล", "Error sending your message") + ": " + (error.message || "Table not found"));
        } finally {
            setLoading(false);
        }
    };

    const subjectOptions = [
        { value: "topup", label: t("เติมเครดิตไม่ได้", "Credit top-up issue") },
        { value: "general", label: t("สอบถามทั่วไป", "General Inquiry") },
        { value: "other", label: t("อื่นๆ", "Others") }
    ];

    return (
        <div className="min-h-screen bg-background">
            <Navbar />
            <main className="pt-24 pb-16">
                <div className="container mx-auto px-4">
                    <header className="text-center mb-12">
                        <h1 className="text-4xl font-bold mb-4">
                            {t("ติดต่อ", "Contact")} <span className="text-gradient">{t("ทีมงาน", "Support")}</span>
                        </h1>
                        <p className="text-muted-foreground max-w-2xl mx-auto">
                            {t(
                                "พบปัญหาการใช้งาน หรือมีข้อสงสัยใดๆ สามารถแจ้งทีมงานของเราได้ผ่านช่องทางด้านล่าง",
                                "Facing issues or have questions? Let us know through any of the channels below."
                            )}
                        </p>
                    </header>

                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 max-w-5xl mx-auto">
                        {/* Contact Form */}
                        <Card variant="glass">
                            <CardHeader>
                                <CardTitle className="flex items-center gap-2">
                                    <Mail className="h-5 w-5 text-primary" />
                                    {t("ส่งข้อความถึงเรา", "Send us a Message")}
                                </CardTitle>
                                <CardDescription>
                                    {t("เราจะติดต่อกลับหาคุณผ่านอีเมลโดยเร็วที่สุด", "We'll get back to you via email as soon as possible.")}
                                </CardDescription>
                            </CardHeader>
                            <CardContent>
                                <form onSubmit={handleSubmit} className="space-y-6">
                                    <div className="space-y-2">
                                        <Label htmlFor="email">{t("อีเมลของคุณ", "Your Email")}</Label>
                                        <Input
                                            id="email"
                                            type="email"
                                            placeholder="example@email.com"
                                            value={formData.email}
                                            onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                                            required
                                        />
                                    </div>

                                    <div className="space-y-2">
                                        <Label htmlFor="subject">{t("หัวข้อปัญหา", "Subject")}</Label>
                                        <Select
                                            value={formData.subject}
                                            onValueChange={(val) => setFormData({ ...formData, subject: val })}
                                        >
                                            <SelectTrigger>
                                                <SelectValue placeholder={t("เลือกหัวข้อ", "Select Subject")} />
                                            </SelectTrigger>
                                            <SelectContent>
                                                {subjectOptions.map(opt => (
                                                    <SelectItem key={opt.value} value={opt.value}>
                                                        {opt.label}
                                                    </SelectItem>
                                                ))}
                                            </SelectContent>
                                        </Select>
                                    </div>

                                    <div className="space-y-2">
                                        <Label htmlFor="message">{t("รายละเอียด", "Message")}</Label>
                                        <Textarea
                                            id="message"
                                            placeholder={t("กรอกรายละเอียดปัญหาของคุณ...", "Describe your issue...")}
                                            className="min-h-[150px]"
                                            value={formData.message}
                                            onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                                            required
                                        />
                                    </div>

                                    <Button type="submit" variant="hero" className="w-full" disabled={loading}>
                                        {loading ? (
                                            <>
                                                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                                                {t("กำลังส่ง...", "Sending...")}
                                            </>
                                        ) : (
                                            <>
                                                <Send className="h-4 w-4 mr-2" />
                                                {t("ส่งข้อความ", "Send Message")}
                                            </>
                                        )}
                                    </Button>
                                </form>
                            </CardContent>
                        </Card>

                        {/* Social Channels */}
                        <div className="space-y-6">
                            <Card variant="glass" className="border-primary/20 bg-primary/5">
                                <CardHeader>
                                    <CardTitle className="flex items-center gap-2 text-primary">
                                        <Facebook className="h-5 w-5" />
                                        {t("ช่องทางการติดต่อด่วน", "Quick Contact")}
                                    </CardTitle>
                                </CardHeader>
                                <CardContent className="space-y-4">
                                    <p className="text-sm text-muted-foreground mb-6">
                                        {t(
                                            "คุณสามารถติดต่อทีมงานผ่าน Facebook Messenger เพื่อความรวดเร็วในการตรวจสอบปัญหาและเติมเครดิต",
                                            "You can contact our team via Facebook Messenger for faster support and credit top-ups."
                                        )}
                                    </p>

                                    <div className="grid gap-4">
                                        <a
                                            href="https://m.facebook.com/messages/compose?ids=61586298720401"
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="group"
                                        >
                                            <Button variant="outline" className="w-full justify-between h-auto py-4 px-6 hover:border-primary/50 transition-all">
                                                <div className="flex items-center gap-3">
                                                    <MessageSquare className="h-5 w-5 text-primary" />
                                                    <div className="text-left">
                                                        <div className="font-bold">Messenger</div>
                                                        <div className="text-xs text-muted-foreground">{t("คุยกับทีมงานโดยตรง", "Chat with Support")}</div>
                                                    </div>
                                                </div>
                                                <LinkIcon className="h-4 w-4 text-muted-foreground group-hover:text-primary" />
                                            </Button>
                                        </a>

                                        <a
                                            href="https://www.facebook.com/profile.php?id=61586298720401"
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="group"
                                        >
                                            <Button variant="outline" className="w-full justify-between h-auto py-4 px-6 hover:border-primary/50 transition-all">
                                                <div className="flex items-center gap-3">
                                                    <Facebook className="h-5 w-5 text-primary" />
                                                    <div className="text-left">
                                                        <div className="font-bold">Facebook Page</div>
                                                        <div className="text-xs text-muted-foreground">{t("ติดตามข่าวสารและอัพเดท", "Follow updates")}</div>
                                                    </div>
                                                </div>
                                                <LinkIcon className="h-4 w-4 text-muted-foreground group-hover:text-primary" />
                                            </Button>
                                        </a>
                                    </div>
                                </CardContent>
                            </Card>

                            <Card variant="glass">
                                <CardContent className="p-6">
                                    <div className="flex items-start gap-4">
                                        <div className="p-3 rounded-xl bg-primary/10 text-primary">
                                            <Mail className="h-6 w-6" />
                                        </div>
                                        <div>
                                            <h3 className="font-bold mb-1">{t("อีเมลสายตรง", "Direct Email")}</h3>
                                            <p className="text-sm text-muted-foreground mb-2">support@gencnx.com</p>
                                            <p className="text-xs text-muted-foreground">
                                                {t("สำหรับการสอบถามที่เป็นทางการหรือความร่วมมือทางธุรกิจ", "For formal inquiries or business partnerships.")}
                                            </p>
                                        </div>
                                    </div>
                                </CardContent>
                            </Card>
                        </div>
                    </div>
                </div>
            </main>
            <Footer />
        </div>
    );
};

export default Contact;
